export interface MatchSummary {
  id: string;
  date: string;
  opponent: string;
  location?: string;
  championship?: string;
  scoreUs: number;
  scoreThem: number;
  isFinished: boolean;
  currentSetNumber?: number;
  currentSetScoreUs?: number;
  currentSetScoreThem?: number;
  teamId?: string;
  /** `season` distingue deux équipes homonymes de saisons différentes. */
  team?: {
    id: string;
    name: string;
    season?: string | null;
    /** Team rule for players (VOL-178); absent = visible. */
    playersSeeOwnMatches?: boolean;
    playersSeeScoutingMatches?: boolean;
    /** Read-only team, over the plan quota (VOL-188). */
    locked?: boolean;
  };
  /** `actions` (active ones) comes with the list only, and not from a backend older than VOL-209. */
  _count?: { sets: number; actions?: number };
  /**
   * Sets enregistrés — présents sur le détail (`GET /matches/:id`), absents de
   * la liste. Sert au pré-remplissage de la modale de clôture.
   */
  sets?: { setNumber: number; scoreUs: number; scoreThem: number }[];
  setsToWinMatch?: number;
  kind?: 'OWN' | 'SCOUTING' | string;
  videoUrl?: string | null;
  /** Override of the team rule for players (VOL-178): `null` follows the rule. */
  playerVisibility?: boolean | null;
  /** Visible to players, rule and override applied (computed by the API). */
  visibleToPlayers?: boolean;
}

export interface Club {
  id: string;
  name: string;
  city: string | null;
}

export const Role = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  CLUB_ADMIN: 'CLUB_ADMIN',
  COACH: 'COACH',
  PLAYER: 'PLAYER',
} as const;

export type Role = typeof Role[keyof typeof Role];

export interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  emailVerified: boolean;
  club: {
    id: string;
    name: string;
    city: string | null;
  } | null;
}

export interface Player {
  id: string;
  name: string;
  number: number;
  role: string;
  isLibero: boolean;
}

export interface GameState {
  scoreUs: number;
  scoreThem: number;
  currentSet: number;
  isServing: boolean;
  rotation: Player[];
  substitutions: number;
  timeOuts: number;
  history: GameEvent[];
}

export interface GameEvent {
  id: string;
  type: string;
  player?: string;
  result?: string;
  score?: [number, number];
  timestamp: number;
  middleBlockerCall?: string;
  passType?: string;
}

export interface VolleyRow {
  id: string;
  set: number;
  scoreUs: number;
  scoreThem: number;
  joueur: string;
  role: string;
  action: string;
  zone: string;
  phase: string;
  middleBlockerCall?: string;
  passType?: string;
  serviceType?: string;
  isReversal?: boolean;
  rotation?: string;
  qualite: string;
  note: number;
  heure: string;
  targetX?: number;
  targetY?: number;
  originX?: number;
  originY?: number;
  touchedByBlock?: boolean;
}

export interface FilterState {
  sets: number[];
  joueurs: string[];
  roles: string[];
  actions: string[];
  zones: string[];
  qualites: string[];
}

export type ChampionshipLevel =
  | 'Ligue des Champions'
  | 'Coupe CEV'
  | 'Challenge Cup'
  | 'Ligue A'
  | 'Pro B'
  | 'Élite'
  | 'Nationale 2'
  | 'Nationale 3'
  | 'Pré-Nationale'
  | 'Régionale 1'
  | 'Régionale 2'
  | 'Départementale'
  | 'Loisir'
  | 'Coupe de France'
  | 'Amical'
  | 'U21'
  | 'U18'
  | 'U15'
  | 'U13';

export const CHAMPIONSHIPS: ChampionshipLevel[] = [
  'Ligue des Champions',
  'Coupe CEV',
  'Challenge Cup',
  'Ligue A',
  'Pro B',
  'Élite',
  'Nationale 2',
  'Nationale 3',
  'Pré-Nationale',
  'Régionale 1',
  'Régionale 2',
  'Départementale',
  'Loisir',
  'Coupe de France',
  'Amical',
  'U21',
  'U18',
  'U15',
  'U13',
];

export type TeamGender = 'M' | 'F' | 'Mixte';

export interface Team {
  id: string;
  name: string;
  gender: TeamGender;
}

/** Libellé de l'équipe de démonstration — miroir du backend (`ownership.helper.ts`). */
export const DEMO_TEAM_NAME = 'Équipe Démo';

/**
 * Une équipe démo ne doit pas être sélectionnable pour l'import DVW ni pour démarrer un match.
 * The backend's `isDemo` wins: a coach may name his own team the same way (VOL-319);
 * the name only decides for an older API response without it.
 */
export function isDemoTeam(team: { name?: string | null; isDemo?: boolean }): boolean {
  if (team.isDemo !== undefined) return team.isDemo;
  return team.name?.trim() === DEMO_TEAM_NAME;
}

export type ActionOutcome = 'POINT' | 'FAULT' | 'CONTINUE';

export type ComplexPhase = 'K1' | 'K2' | 'K3';

export type RotationPosition = 'P1' | 'P2' | 'P3' | 'P4' | 'P5' | 'P6';

export interface Rally {
  id: string;
  setId: string;
  startTime: number;
  endTime?: number;
  startScoreUs: number;
  startScoreThem: number;
  servingTeam: 'US' | 'THEM';
  rotationUs: RotationPosition;
  actions: GameEvent[];
  winner?: 'US' | 'THEM';
  complex: ComplexPhase;
}

export interface DetailedAction extends GameEvent {
  phase: ComplexPhase;
  quality: string;
  startZone?: string;
  endZone?: string;
  skill: string;
}

